/* eslint-disable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content -- react-markdown supplies children through component props */
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import ReactPlayer from "react-player";
import TableOfContents from "../components/TableOfContents";
import abstract from "../assets/photography/abstract.jpg";
import alpina from "../assets/photography/alpina.jpg";
import alpinaBlue from "../assets/photography/alpinaBlue.jpg";
import cloak from "../assets/photography/cloak.jpg";
import corvette from "../assets/photography/corvette.jpg";
import countach from "../assets/photography/countach.jpg";
import csl from "../assets/photography/csl.jpg";
import delorean1 from "../assets/photography/delorean1.jpg";
import delorean2 from "../assets/photography/delorean2.jpg";
import delorean3 from "../assets/photography/delorean3.jpg";
import f40 from "../assets/photography/f40.jpg";
import ferrari from "../assets/photography/ferrari.jpg";
import garage1 from "../assets/photography/garage1.jpg";
import garage2 from "../assets/photography/garage2.jpg";
import gt3 from "../assets/photography/gt3.jpg";
import lancia from "../assets/photography/lancia.jpg";
import leather from "../assets/photography/leather.jpg";
import nissanZ from "../assets/photography/nissanZ.jpg";
import orange from "../assets/photography/orange.jpg";
import porscheDetail from "../assets/photography/porscheDetail.jpg";
import project7 from "../assets/photography/project7.jpg";
import steering from "../assets/photography/steering.jpg";
import wheels from "../assets/photography/wheels.jpg";
import { useEffect } from "react";

const images = {};

const content = `

My love for taking photos first started when I watched my Dad tinker with his camera on family vacations throughout my childhood. At the same time, I became obsessed with automobiles and began attending car shows, eventually bringing my camera along. This page is a collection of my favorite shots, mostly of cars.

`;

const Photography = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	return (
		<div
			className="flex flex-col w-full min-h-screen items-center p-4 lg:p-20 bg-[#010101] text-[#dd362a]"
			style={{
				paddingLeft: "max(1.25rem, calc(var(--toc-right-edge, 0px) + 1rem))",
			}}
		>
			<TableOfContents
				content={content}
				pageName="Photography"
				accentColor="#dd362a"
				homeOnly={true}
			/>
			<div className="flex flex-col lg:flex-row header w-full max-w-[1000px] p-4 lg:p-20 bg-[#010101]">
				<div className="flex flex-col w-full lg:min-w-[400px] lg:max-w-[400px]">
					<h1 className="text-[52px] lg:text-[120px] font-medium mb-4 lg:tracking-tight tracking-[-0.04em] lg:mt-0 mt-12">
						Photography
					</h1>
				</div>
			</div>
			<div className="body w-full lg:pt-10 max-w-[1000px] p-4 lg:p-20 rounded-[50px]">
				<Markdown
					rehypePlugins={[rehypeRaw]}
					components={{
						h1: ({ node, ...props }) => {
							const text =
								typeof props.children === "string" ? props.children : "";
							const id = text
								.toLowerCase()
								.replace(/[^a-z0-9]+/g, "-")
								.replace(/(^-|-$)/g, "");
							return (
								<h1
									id={id}
									className="text-4xl font-bold my-8 text-center"
									{...props}
								/>
							);
						},
						h2: ({ node, ...props }) => {
							const text =
								typeof props.children === "string" ? props.children : "";
							const id = text
								.toLowerCase()
								.replace(/[^a-z0-9]+/g, "-")
								.replace(/(^-|-$)/g, "");
							return (
								<h2
									id={id}
									className="text-3xl font-bold mb-6 mt-4"
									{...props}
								/>
							);
						},
						h3: ({ node, ...props }) => {
							const text =
								typeof props.children === "string" ? props.children : "";
							const id = text
								.toLowerCase()
								.replace(/[^a-z0-9]+/g, "-")
								.replace(/(^-|-$)/g, "");
							return (
								<h3
									id={id}
									className="text-2xl font-semibold mb-2"
									{...props}
								/>
							);
						},
						h4: ({ node, ...props }) => (
							<h4
								className="text-2xl font-semibold mb-2 leading-tight"
								{...props}
							/>
						),
						h5: ({ node, ...props }) => (
							<h5 className="text-2xl font-medium mb-2" {...props} />
						),
						h6: ({ node, ...props }) => (
							<h6
								className="text-sm italic text-center mt-[-32px] mb-8"
								{...props}
							/>
						),
						p: ({ node, ...props }) => (
							<p
								className="text-xl mb-4 leading-tight text-[#dd362a] font-medium"
								{...props}
							/>
						),
						a: ({ node, ...props }) => (
							<a
								className="text-[#FF5C00] hover:text-[#FF5C00] underline"
								target="_blank"
								rel="noopener noreferrer"
								{...props}
							/>
						),
						ul: ({ node, ...props }) => (
							<ul className="list-disc list-inside mb-4 ml-4" {...props} />
						),
						img: ({ node, ...props }) => {
							const imageSrc = images[props.src] || props.src;
							if (imageSrc.endsWith(".mp4")) {
								return (
									<ReactPlayer
										url={imageSrc}
										controls
										className="my-12 rounded-2xl mx-auto"
									/>
								);
							}
							return (
								<img
									src={imageSrc}
									alt={props.alt}
									className="h-full object-contain my-12 max-h-[500px] rounded-2xl mx-auto"
								/>
							);
						},
					}}
				>
					{content}
				</Markdown>
				<div className="flex flex-col w-full mt-12 lg:mt-20 gap-y-8 lg:gap-y-12">
					<img
						src={garage1}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={garage2}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={lancia}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={cloak}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={csl}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={ferrari}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={project7}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={wheels}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={alpina}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={f40}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={abstract}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={orange}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={nissanZ}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={delorean1}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={delorean2}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={delorean3}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={countach}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={corvette}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={gt3}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={porscheDetail}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={leather}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={alpinaBlue}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
					<img
						src={steering}
						alt="noise"
						className="w-full object-contain border-white border-8"
					/>
				</div>
			</div>
		</div>
	);
};

export default Photography;
